import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Sub-page navigation as a row of tabs at the top of the page, drawn like the
 * selectors on teral.dev: the current page is an ink pill.
 */
function SecondaryNav({ className, children, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      className={cn(
        "mx-auto flex w-full max-w-6xl shrink-0 items-center gap-1 overflow-x-auto px-6 pt-6 scrollbar-hide",
        className,
      )}
      {...props}
    >
      {children}
    </nav>
  );
}

function SecondaryNavTitle({ className, ...props }: React.ComponentProps<"h2">) {
  return <h2 className={cn("sr-only", className)} {...props} />;
}

function SecondaryNavGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-[10px] border bg-raised p-1 shadow-xs",
        className,
      )}
      {...props}
    />
  );
}

const secondaryNavItemVariants = cva(
  "flex h-7 shrink-0 items-center whitespace-nowrap rounded-[7px] px-3 text-sm transition-colors outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--focus-ring)",
  {
    variants: {
      active: {
        true: "bg-gray-12 font-medium text-gray-1",
        false: "text-gray-11 hover:bg-grayA-3 hover:text-gray-12",
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

type SecondaryNavItemProps = useRender.ComponentProps<"a"> &
  VariantProps<typeof secondaryNavItemVariants>;

function SecondaryNavItem({ className, active, render, ...props }: SecondaryNavItemProps) {
  return useRender({
    defaultTagName: "a",
    render,
    props: mergeProps<"a">(
      {
        "aria-current": active ? "page" : undefined,
        className: cn(secondaryNavItemVariants({ active }), className),
      } as React.ComponentProps<"a">,
      props,
    ),
  });
}

export {
  SecondaryNav,
  SecondaryNavTitle,
  SecondaryNavGroup,
  SecondaryNavItem,
  secondaryNavItemVariants,
};
